import { GenericType } from "@sbd-doug-frontend/shared/lib/ui/quick-search/quick-search.types";
import { CIPXQuoteRequestIntakeForm, CIPXQuoteRequestIntakeFormBondFamily, CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration, CIPXQuoteRequestIntakeFormStateBond, CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionSet, CIPXQuoteRequestIntakeFormStateBondDetailedQuestion, CIPXQuoteRequestIntakeFormStateBondQuestionValidationArgument } from "./intake-form.types";
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { notEmptyOrSpaces } from "@sbd-doug-frontend/shared/lib/public-validators";
import { removeAllControls } from "@sbd-doug-frontend/shared/lib/public-forms";
import { AbstractControl, ValidationErrors } from "@angular/forms";
import { CIPXQuoteRequestIntakeFormStateBondQuestionValidationRule, CIPXQuoteRequestIntakeFormStateBondQuestionValidationSuccess } from "./intake-form.types";
import { DateTime } from "luxon";

export function isCIPXQuoteRequestIntakeFormBondFamily(
  obj: any
): obj is CIPXQuoteRequestIntakeFormBondFamily {
  // Check the object is not null and is an object
  if (obj == null || typeof obj !== 'object') {
    return false;
  }

  // Check required fields
  return (
    typeof (obj as CIPXQuoteRequestIntakeFormBondFamily).bond_family_label === 'string' &&
    typeof (obj as CIPXQuoteRequestIntakeFormBondFamily).grp === 'string'
  );
}

export function isCIPXQuoteRequestIntakeFormStateBond(
  obj: any
): obj is CIPXQuoteRequestIntakeFormStateBond {
  // Check the object is not null and is an object
  if (obj == null || typeof obj !== 'object') {
    return false;
  }

  // Check required fields
  return (
    typeof (obj as CIPXQuoteRequestIntakeFormStateBond).state_bond_id === 'number'
  );
}

export function isGenericType(obj: any): obj is GenericType {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    typeof obj.label === 'string' &&
    typeof obj.value === 'string'
  );
}

export function requiresCredit(stateBond: CIPXQuoteRequestIntakeFormStateBond): boolean {
  return stateBond.requires_credit_check;
}

export function configureFormValidationByStateBondRequirements(
  form: FormGroup<CIPXQuoteRequestIntakeForm>,
  stateBond: CIPXQuoteRequestIntakeFormStateBond
): FormGroup<CIPXQuoteRequestIntakeForm> {
  // Remove all existing additional question controls.
  removeAllControls(form.controls.additional_questions);

  if (!stateBond.additional_questions) {
    return form;
  }

  const additionalQuestionsControl = form.controls.additional_questions as FormGroup;

  stateBond.additional_questions.forEach(section => {
    section.question_sets.forEach(questionSet => {
      if (!questionSet.questions || questionSet.questions.length === 0) {
        return;
      }

      if (isCollectionQuestionSet(questionSet)) {
        const questions = applyQuestionSetOverrides(questionSet);
        const items = new FormArray<FormGroup>([]);
        const minItems = Math.max(1, questionSet.question_set.collection?.min_items ?? 1);
        for (let i = 0; i < minItems; i++) {
          items.push(createCollectionItemGroup(questions));
        }
        setQuestionControl(additionalQuestionsControl, questionSet.question_set_code, items);
        return;
      }

      questionSet.questions.forEach(question => {
        setQuestionControl(
          additionalQuestionsControl,
          question.question_code,
          createQuestionControl(question, additionalQuestionsControl)
        );
      });
    });
  });

  return form;
}

export function isCollectionQuestionSet(questionSet: CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionSet): boolean {
  return questionSet.question_set?.layout === 'collection';
}

export function isDateQuestion(question: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion): boolean {
  return question.question_type === 'date' || question.display?.control === 'date';
}

export function isEmailQuestion(question: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion): boolean {
  return question.question_type === 'email' || question.display?.control === 'email';
}

export function blockNegativeNumberKeys(event: KeyboardEvent): void {
  if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
    event.preventDefault();
  }
}

export function getQuestionNumericBounds(
  question: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion
): { min?: number; max?: number } {
  const validation = question.validation;
  if (!validation) {
    return {};
  }

  let min = validation.min;
  let max = validation.max;
  for (const arg of validation.arguments ?? []) {
    if (min === undefined && arg.min !== undefined) {
      min = arg.min;
    }
    if (max === undefined && arg.max !== undefined) {
      max = arg.max;
    }
  }
  return { min, max };
}

/**
 * Creates the form control for a single question, wiring validators against
 * `contextGroup` (the group holding this question's siblings): the flat
 * additional_questions group for scalar sets, or the item group for
 * collection items.
 */
export function createQuestionControl(
  question: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion,
  contextGroup: FormGroup
): FormControl | FormGroup {
  if (question.question_type === 'address') {
    return createAddressQuestionGroup();
  }

  const validators = buildQuestionValidators(question, contextGroup);
  if (isEmailQuestion(question)) {
    validators.push(Validators.email);
  }

  if (question.question_type === 'bool') {
    return new FormControl<boolean | null>(false, validators);
  }
  if (isDateQuestion(question) || isEmailQuestion(question)) {
    return new FormControl<string | null>(null, validators);
  }
  if (question.question_type === 'string') {
    return new FormControl<string | null>(null, validators);
  }
  if (['number', 'integer', 'decimal', 'float', 'int', 'currency'].includes(question.question_type)) {
    return new FormControl<number | null>(null, validators);
  }
  if (question.question_type === 'user_selected_option') {
    return new FormControl<string | null>(question.user_selected_options?.default ?? null, validators);
  }
  return new FormControl<string>('', validators);
}

/**
 * One FormGroup per collection item; each child question's validators resolve
 * dependents against its own item group.
 */
export function createCollectionItemGroup(
  questions: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion[]
): FormGroup {
  const group = new FormGroup({});
  questions.forEach(question => {
    setQuestionControl(group, question.question_code, createQuestionControl(question, group));
  });
  return group;
}

function setQuestionControl(group: FormGroup, name: string, control: AbstractControl): void {
  if (group.contains(name)) {
    group.setControl(name, control);
  } else {
    group.addControl(name, control);
  }
}

function padDatePart(value: number): string {
  return String(value).padStart(2, '0');
}

function toLocalDateParts(value: unknown): { year: number; month: number; day: number } | null {
  if (value == null || value === '') {
    return null;
  }

  if (DateTime.isDateTime(value) && value.isValid) {
    return { year: value.year, month: value.month, day: value.day };
  }

  if (value instanceof Date && !isNaN(value.getTime())) {
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
  }

  if (typeof value === 'string') {
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      return { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) };
    }
    const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (us) {
      let year = Number(us[3]);
      if (year < 100) {
        year += year >= 50 ? 1900 : 2000;
      }
      return { year, month: Number(us[1]), day: Number(us[2]) };
    }
  }

  return null;
}

export function formatLocalCalendarDate(value: unknown): string | null {
  const parts = toLocalDateParts(value);
  if (!parts) {
    return null;
  }
  return `${parts.year}-${padDatePart(parts.month)}-${padDatePart(parts.day)}`;
}

export function formatIntakeQuestionDate(value: unknown): string | null {
  const date = formatLocalCalendarDate(value);
  return date ? `${date}T12:00:00` : null;
}

/**
 * Matches the intake processor's address resolver input shape.
 */
function createAddressQuestionGroup(): FormGroup {
  return new FormGroup({
    street_1: new FormControl<string | null>(null, [Validators.required]),
    street_2: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null, [Validators.required]),
    state_abbreviation: new FormControl<string | null>(null, [Validators.required]),
    zip: new FormControl<string | null>(null, [Validators.required]),
  });
}

function buildQuestionValidators(
  question: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion,
  contextGroup: FormGroup
): ValidatorFn[] {
  const validators: ValidatorFn[] = [];

  if (!question.validation) {
    return validators;
  }

  const defaultRule = question.validation;

  if (defaultRule.validation_rule) {
    switch (defaultRule.validation_rule) {
      case 'notempty':
        validators.push(Validators.required);
        break;
      case 'notemptyorspaces':
        validators.push(isDateQuestion(question) ? Validators.required : notEmptyOrSpaces);
        break;
      case 'txtaxpayerid':
        validators.push(Validators.pattern(/^\d{11}$/));
        break;
      case 'fein':
        validators.push(Validators.pattern(/^\d{2}-?\d{7}$/));
        break;
      case 'email':
        validators.push(Validators.email);
        break;
      case 'explainerquestion':
        if (defaultRule.arguments) {
          validators.push(dependentAnswerRequiredValidator(defaultRule.arguments, contextGroup));
        }
        break;
    }
  }

  const { min, max } = getQuestionNumericBounds(question);
  if (min !== undefined) {
    validators.push(Validators.min(min));
  }
  if (max !== undefined) {
    validators.push(Validators.max(max));
  }

  if (defaultRule.rules) {
    defaultRule.rules.forEach(ruleItem => {
      if (!ruleItem.rule || !ruleItem.on_success) {
        return;
      }

      const condition = createConditionFromRule(ruleItem.rule);
      if (condition) {
        const successAction = createSuccessAction(ruleItem.on_success);
        if (successAction) {
          validators.push(createConditionalValidator(condition, successAction, contextGroup));
        }
      }
    });
  }

  return validators;
}

/**
 * Requires an answer when any dependent question matches its assessed_value
 * (or is truthy when no assessed_value is configured). Radio questions store
 * strings like "Yes"/"No"/"N/A", so truthiness alone is not enough.
 */
function dependentAnswerRequiredValidator(
  args: CIPXQuoteRequestIntakeFormStateBondQuestionValidationArgument[],
  contextGroup: FormGroup
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    dependentAnswersTriggered(args, contextGroup) ? Validators.required(control) : null;
}

function dependentAnswersTriggered(
  args: CIPXQuoteRequestIntakeFormStateBondQuestionValidationArgument[],
  contextGroup: FormGroup
): boolean {
  return args.some(arg => {
    if (!arg.question_code) {
      return false;
    }
    const value = contextGroup.get(arg.question_code)?.value;
    return arg.assessed_value !== undefined ? value === arg.assessed_value : !!value;
  });
}

/**
 * Whether a question should currently render, given its sibling answers in
 * `contextGroup`. Honors display.when, falling back to the legacy
 * explainer-question gate.
 */
export function isQuestionVisible(
  question: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion,
  contextGroup: FormGroup
): boolean {
  const when = question.display?.when;
  if (when?.question_code) {
    const value = contextGroup.get(when.question_code)?.value;
    return when.operator === 'neq' ? value !== when.value : value === when.value;
  }

  if (question.validation?.validation_rule === 'explainerquestion' && question.validation.arguments) {
    return dependentAnswersTriggered(question.validation.arguments, contextGroup);
  }

  return true;
}

// Helper function to create a condition from a rule
function createConditionFromRule(rule: CIPXQuoteRequestIntakeFormStateBondQuestionValidationRule): any {
  if (!rule.type || !rule.source || !rule.assessed_value) {
    return null;
  }

  return {
    source: rule.source,
    type: rule.type,
    value: rule.assessed_value
  };
}

// Helper function to create a success action
function createSuccessAction(onSuccess: CIPXQuoteRequestIntakeFormStateBondQuestionValidationSuccess): any {
  if (!onSuccess.validation_rule) {
    return null;
  }

  return {
    validation_rule: onSuccess.validation_rule,
    arguments: onSuccess.arguments
  };
}

// Helper function to create a conditional validator
function createConditionalValidator(condition: any, successAction: any, formGroup: FormGroup): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const sourceControl = formGroup.get(condition.source);
    if (!sourceControl) {
      return null;
    }

    const sourceValue = sourceControl.value;
    let conditionMet = false;

    switch (condition.type) {
      case 'eq':
        conditionMet = sourceValue === condition.value;
        break;
      case 'neq':
        conditionMet = sourceValue !== condition.value;
        break;
    }

    if (conditionMet) {
      switch (successAction.validation_rule) {
        case 'notempty':
          return Validators.required(control);
        case 'notemptyorspaces':
          return notEmptyOrSpaces(control);
        case 'txtaxpayerid':
          return Validators.pattern(/^\d{11}$/)(control);
        case 'fein':
          return Validators.pattern(/^\d{2}-?\d{7}$/)(control);
        case 'explainerquestion':
          if (successAction.arguments) {
            return dependentAnswerRequiredValidator(successAction.arguments, formGroup)(control);
          }
          break;
      }
    }

    return null;
  };
}

export function sectionHasQuestions(sectionName: string, stateBond: CIPXQuoteRequestIntakeFormStateBond | null): boolean {
  if (stateBond === null || !stateBond.additional_questions || stateBond.additional_questions.length === 0) {
    return false;
  }

  // Check if the section exists and has question sets with questions
  const section = stateBond.additional_questions.find(s => s.section === sectionName);
  if (!section) {
    return false;
  }

  return section.question_sets.some(questionSet =>
    questionSet.questions && questionSet.questions.length > 0
  );
}

/**
 * Merges the set-level overrides (question_text, labels, etc. declared in
 * question_set.questions) into the set's detailed questions. Overrides are
 * resolved per set, so the same question code can carry different text in
 * different sets (e.g. "ward" vs "deceased" collections).
 */
export function applyQuestionSetOverrides(
  questionSet: CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionSet
): CIPXQuoteRequestIntakeFormStateBondDetailedQuestion[] {
  if (!questionSet.questions || questionSet.questions.length === 0) {
    return [];
  }

  const overridesByCode = new Map<string, Partial<CIPXQuoteRequestIntakeFormStateBondDetailedQuestion>>();
  (questionSet.question_set?.questions ?? []).forEach(q => {
    const override: Partial<CIPXQuoteRequestIntakeFormStateBondDetailedQuestion> = {};
    if (q.question_text !== undefined) {
      override.question_text = q.question_text;
    }
    if (q.question_help_text !== undefined) {
      override.question_help_text = q.question_help_text;
    }
    if (q.question_label !== undefined) {
      override.question_label = q.question_label;
    }
    if (q.question_placeholder !== undefined) {
      override.question_placeholder = q.question_placeholder;
    }
    if (Object.keys(override).length > 0) {
      overridesByCode.set(q.question_code, override);
    }
  });

  return questionSet.questions.map(question => ({
    ...question,
    ...(overridesByCode.get(question.question_code) ?? {})
  }));
}

/**
 * Flat (non-collection) questions for a section, with per-set overrides applied.
 */
export function getQuestionsForSection(
  sectionName: string,
  stateBond: CIPXQuoteRequestIntakeFormStateBond
): CIPXQuoteRequestIntakeFormStateBondDetailedQuestion[] {
  const section = stateBond.additional_questions?.find(s => s.section === sectionName);
  if (!section) {
    return [];
  }

  return section.question_sets
    .filter(questionSet => !isCollectionQuestionSet(questionSet))
    .flatMap(questionSet => applyQuestionSetOverrides(questionSet));
}

/**
 * Collection-layout sets for a section, with per-set overrides merged into
 * each set's detailed questions.
 */
export function getCollectionSetsForSection(
  sectionName: string,
  stateBond: CIPXQuoteRequestIntakeFormStateBond
): CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionSet[] {
  const section = stateBond.additional_questions?.find(s => s.section === sectionName);
  if (!section) {
    return [];
  }

  return section.question_sets
    .filter(questionSet => isCollectionQuestionSet(questionSet) && questionSet.questions?.length > 0)
    .map(questionSet => ({
      ...questionSet,
      questions: applyQuestionSetOverrides(questionSet)
    }));
}

export function configureIntakeFormByPricingConfiguration(form: FormGroup<CIPXQuoteRequestIntakeForm>, configuration: CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration) {
  if(configuration.penalty && configuration.penalty.default_penalty) {
    form.controls.bondLookup.controls.bond_amount.setValue(configuration.penalty.default_penalty);
  } else {
    form.controls.bondLookup.controls.bond_amount.setValue(null);
  }


}
